/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AccessControlAction } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface FirewallRuleProps {
  id: string;
  name: string;
  action: AccessControlAction;
  ipRange: string;
  enabled: boolean;
  companyId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class FirewallRuleRecord extends Record<FirewallRuleProps>({
  id: null as any,
  name: null as any,
  action: null as any,
  ipRange: null as any,
  enabled: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    action: AccessControlAction;
    ipRange: string;
    enabled: boolean;
    companyId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): FirewallRuleRecord {
    if (json == null) {
      throw new Error('FirewallRuleRecord.fromJS: parameter cannot be null');
    }
    return new FirewallRuleRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'FirewallRuleJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'FirewallRuleJson.name'),
      action: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'action', AccessControlAction.Deny),
        'FirewallRuleJson.action',
      ),
      ipRange: getFromOrThrow(json, (j) => j.ipRange, 'FirewallRuleJson.ipRange'),
      enabled: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'enabled', false),
        'FirewallRuleJson.enabled',
      ),
      companyId: json.companyId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    action: AccessControlAction;
    ipRange: string;
    enabled: boolean;
    companyId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [FirewallRuleRecord, Error[]] {
    if (json == null) {
      throw new Error('FirewallRuleRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new FirewallRuleRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'FirewallRuleJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'FirewallRuleJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      action: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'action', AccessControlAction.Deny),
            'FirewallRuleJson.action',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ipRange: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.ipRange, 'FirewallRuleJson.ipRange'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      enabled: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'enabled', false),
            'FirewallRuleJson.enabled',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () => json.companyId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: FirewallRuleProps) {
    super(props);
    if (props == null) {
      throw new Error('FirewallRuleRecord constructor parameter cannot be null');
    }
  }
}

export abstract class FirewallRuleViewModelBase implements FirewallRuleProps {
  protected readonly _props: FirewallRuleProps;
  private _json?: FirewallRuleProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get action(): AccessControlAction {
    return this._props.action;
  }
  get ipRange(): string {
    return this._props.ipRange;
  }
  get enabled(): boolean {
    return this._props.enabled;
  }
  get companyId(): string | null {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAllow() {
    return this._props.action === AccessControlAction.Allow;
  }
  public get isDeny() {
    return this._props.action === AccessControlAction.Deny;
  }

  public isAnyAction(...accessControlAction: AccessControlAction[]) {
    return accessControlAction.includes(this._props.action);
  }

  constructor(props: FirewallRuleProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        action: this._props.action,
        ipRange: this._props.ipRange,
        enabled: this._props.enabled,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllFirewallRuleEntities = (state: AppState) =>
  state.entityStores.firewallRule.entities;

export const selectFirewallRuleById = (id: string) => (state: AppState) =>
  state.entityStores.firewallRule.entities.get(id);

export const selectFirewallRuleMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.firewallRule.meta.get(id);

export const selectFirewallRuleAndMetadataById = (id: string) =>
  createSelector(
    selectFirewallRuleById(id),
    selectFirewallRuleMetadataById(id),
    (firewallRule, meta) => ({ firewallRule, meta }),
  );

export function selectFirewallRuleEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllFirewallRuleEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, FirewallRuleRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectFirewallRuleEntitiesByName(name: string) {
  return createSelector(selectAllFirewallRuleEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectFirewallRuleEntitiesByAction(action: AccessControlAction) {
  return createSelector(selectAllFirewallRuleEntities, (entities) =>
    entities.filter((e) => e.action === action).valueSeq(),
  );
}

export function selectFirewallRuleEntitiesByIpRange(ipRange: string) {
  return createSelector(selectAllFirewallRuleEntities, (entities) =>
    entities.filter((e) => e.ipRange === ipRange).valueSeq(),
  );
}

export function selectFirewallRuleEntitiesByEnabled(enabled: boolean) {
  return createSelector(selectAllFirewallRuleEntities, (entities) =>
    entities.filter((e) => e.enabled === enabled).valueSeq(),
  );
}

export function selectFirewallRuleEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllFirewallRuleEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
